import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type GalleryShareStatus = 'published' | 'hidden';
export type GalleryShareRole = 'guest' | 'client';

// Owner-only DTO: `/api/gallery/:id/…` share routes all sit behind the server's
// owner/admin check, so they may carry the passcode VALUES the «Поделиться» window
// shows. Never copy these two fields onto a public/client-facing shape.
export interface GalleryShareAccessDTO {
  galleryId: string;
  title: string;
  status: GalleryShareStatus;
  live: boolean;
  clientAccessEnabled: boolean;
  publicUrl: string | null;
  clientUrl: string | null;
  guestPasscodePresent: boolean;
  // false = the guest code is parked: the link opens without a password, the code is kept.
  guestPasscodeEnabled: boolean;
  clientPasscodePresent: boolean;
  // null = not set (or the cipher no longer decrypts — login still works off the hash);
  // the guest code stays here while parked.
  guestPasscode: string | null;
  clientPasscode: string | null;
  // Shared pool cap on served downloads; null = unlimited.
  downloadMaxCount: number | null;
  // Wrong-passcode attempts before a share link locks; null = lockout off.
  passcodeAttemptLimit: number | null;
  // The gallery OWNER's verified custom-domain origin — server-resolved, so an
  // admin collaborator never brands links with their own account's domain.
  customOrigin: string | null;
}

export interface SetSharePasscodeBody {
  role: GalleryShareRole;
  // null clears the passcode; only valid for the guest role (client passcode is mandatory)
  passcode?: string | null;
  // Guest only: false parks the stored code, true re-arms it (mints a 5-digit one when none is stored).
  // `passcode` is ignored when present.
  enabled?: boolean;
  // true = the server mints a fresh 5-digit code for the role — the cabinet never coins one.
  generate?: boolean;
}

export interface SetClientAccessBody {
  enabled: boolean;
}

export type GalleryInviteStatus = 'pending' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'canceled';

export interface GalleryInviteDTO {
  id: string;
  recipientEmail: string;
  subject: string;
  status: GalleryInviteStatus;
  scheduledAt: string | null;
  sentAt: string | null;
  createdAt: string;
  error: string | null;
}

// Account-level reusable composer template (subject + body preset)
export interface InviteTemplateDTO {
  id: string;
  name: string;
  subject: string;
  body: string;
}

export interface SaveInviteTemplateBody {
  name: string;
  subject: string;
  body: string;
}

export interface SendInvitesBody {
  recipients: string[];
  subject: string;
  body: string;
  includeGuestPasscode?: boolean;
  includeClientPasscode?: boolean;
  scheduledAt?: string;
}

// One dispatched (or queued) email for the gallery — the Share tab's sent log.
// `credentials` lists which login details were embedded (kind labels, never the
// raw values), so the log can show what was shared without leaking a passcode.
export interface EmailHistoryEntryDTO {
  id: string;
  toEmail: string;
  kind: string;
  subject: string | null;
  credentials: string[];
  status: 'pending' | 'sent' | 'failed';
  createdAt: string;
  sentAt: string | null;
}

@Injectable({ providedIn: 'root' })
export class GalleryShareApiService {
  private readonly http = inject(HttpClient);

  getShareAccess(galleryId: string): Promise<GalleryShareAccessDTO> {
    return firstValueFrom(this.http.get<GalleryShareAccessDTO>(`/api/gallery/${galleryId}/share-access`));
  }

  setPasscode(galleryId: string, body: SetSharePasscodeBody): Promise<GalleryShareAccessDTO> {
    return firstValueFrom(this.http.put<GalleryShareAccessDTO>(`/api/gallery/${galleryId}/share-passcode`, body));
  }

  setClientAccess(galleryId: string, enabled: boolean): Promise<GalleryShareAccessDTO> {
    const body: SetClientAccessBody = { enabled };
    return firstValueFrom(this.http.put<GalleryShareAccessDTO>(`/api/gallery/${galleryId}/client-access`, body));
  }

  setPasscodeAttemptLimit(galleryId: string, limit: number | null): Promise<GalleryShareAccessDTO> {
    return firstValueFrom(this.http.put<GalleryShareAccessDTO>(`/api/gallery/${galleryId}/passcode-attempt-limit`, { limit }));
  }

  regenerate(galleryId: string): Promise<GalleryShareAccessDTO> {
    return firstValueFrom(this.http.post<GalleryShareAccessDTO>(`/api/gallery/${galleryId}/share-regenerate`, {}));
  }

  sendInvites(galleryId: string, body: SendInvitesBody): Promise<GalleryInviteDTO[]> {
    return firstValueFrom(this.http.post<GalleryInviteDTO[]>(`/api/gallery/${galleryId}/invites`, body));
  }

  getInvites(galleryId: string): Promise<GalleryInviteDTO[]> {
    return firstValueFrom(this.http.get<GalleryInviteDTO[]>(`/api/gallery/${galleryId}/invites`));
  }

  cancelInvite(galleryId: string, inviteId: string): Promise<GalleryInviteDTO[]> {
    return firstValueFrom(this.http.delete<GalleryInviteDTO[]>(`/api/gallery/${galleryId}/invites/${inviteId}`));
  }

  listInviteTemplates(): Promise<InviteTemplateDTO[]> {
    return firstValueFrom(this.http.get<InviteTemplateDTO[]>('/api/gallery/invite-templates'));
  }

  saveInviteTemplate(body: SaveInviteTemplateBody): Promise<InviteTemplateDTO[]> {
    return firstValueFrom(this.http.put<InviteTemplateDTO[]>('/api/gallery/invite-templates', body));
  }

  // Id-addressed update — supports RENAME (the name-keyed upsert above can't).
  updateInviteTemplate(templateId: string, body: SaveInviteTemplateBody): Promise<InviteTemplateDTO[]> {
    return firstValueFrom(this.http.put<InviteTemplateDTO[]>(`/api/gallery/invite-templates/${templateId}`, body));
  }

  deleteInviteTemplate(templateId: string): Promise<InviteTemplateDTO[]> {
    return firstValueFrom(this.http.delete<InviteTemplateDTO[]>(`/api/gallery/invite-templates/${templateId}`));
  }

  getEmailHistory(galleryId: string): Promise<EmailHistoryEntryDTO[]> {
    return firstValueFrom(this.http.get<EmailHistoryEntryDTO[]>(`/api/gallery/${galleryId}/email-history`));
  }
}
